import { httpClient } from 'providers/clients/httpClient';

// core.asset.export.status
export type CoreAssetExportStatus = {
  id?: string;
  queued?: boolean;
  running?: boolean;
  complete?: boolean;
};

// post editorial/export/export
export type PostEditorialExportParams = {
  body: {
    // body params
    uuid: string[];
    notification?: {
      return_url: string;
    };
  };
};

// post editorial/export/export response
export type PostEditorialExportParamsResponse = {
  status?: string | number;
  data?: CoreAssetExportStatus;
};

// post /editorial/export/import
type PostEditorialImportParams = {
  body: {
    fileId: string; // File "string"
  };
};

// post /editorial/export/import response
export type PostEditorialImportParamsResponse = {
  status?: string | number;
  data?: CoreAssetExportStatus;
};

// Editorial Export Task Status (from core.asset.export.status schema)
export type EditorialImportExportTaskStatus = {
  /** Export/import task identifier */
  id: string;
  /** Whether the task is still queued to run */
  queued: boolean;
  /** Whether the task is currently running */
  running: boolean;
  /** Whether the task is complete */
  complete: boolean;
};

// POST RESPONSE /editorial/export/export/
export type PostEditorialImportExportResult = {
  status: 'success' | 'error';
  data: EditorialImportExportTaskStatus;
};

// -----------------------------------------------
// ------------ EXPORT IMPORT PROVIDER --------------
// -----------------------------------------------
export const exportImportProvider = {
  createImportTask: async (
    params: PostEditorialImportParams,
  ): Promise<PostEditorialImportExportResult> => {
    try {
      const id = params?.body?.fileId;

      const payload = {
        file: id,
      };

      const url = `/editorial/export/import/`;

      const { data } = await httpClient({
        url: url,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      return data;
    } catch (error) {
      console.error('[createImportTask] Error:', error);
      throw error;
    }
  },

  createExportTask: async (
    param: PostEditorialExportParams,
  ): Promise<PostEditorialImportExportResult> => {
    try {
      const url = `/editorial/export/export/`;

      const { data } = await httpClient({
        url: url,
        method: 'POST',
        data: JSON.stringify(param.body),
      });

      return data;
    } catch (error) {
      console.error('[createExportTask] Error:', error);
      throw error;
    }
  },

  getExportStatus: async (id: string): Promise<PostEditorialImportExportResult> => {
    try {
      const url = `/editorial/export/export/${id}/`;

      const { data } = await httpClient({
        url: url,
        method: 'GET',
      });

      return data;
    } catch (error) {
      console.error('[getExportState] Error:', error);
      throw error;
    }
  },

  getImportStatus: async (id: string): Promise<PostEditorialImportExportResult> => {
    try {
      const url = `/editorial/export/import/${id}/`;

      const { data } = await httpClient({
        url: url,
        method: 'GET',
      });

      return data;
    } catch (error) {
      console.error('[getImportStatus] Error:', error);
      throw error;
    }
  },

  getExportFile: async (
    id: string,
    domainOverride?: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    try {
      const url = `/editorial/export/export/${id}/download/`;

      const response = await httpClient({
        url: url,
        method: 'GET',
        responseType: 'blob',
        headers: domainOverride
          ? {
              'X-Blox-Domain': domainOverride,
            }
          : undefined,
      });

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export-${id}.bpm`; // Default filename

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error('[getExportFile] Error:', error);
      throw error;
    }
  },
  // set as const - all keys (functions) in this object
  // are readonly and we should to ensure they
  // are immutable / cant be accidentally modified
} as const;

// Since all functions have their own signature with types
// we can safely use typeof helper to create the type for us
// and then '&' add it to the BloxCustomDataProvider type
export type ExportImportProviderType = typeof exportImportProvider;
